import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { EditorState, Modifier, SelectionState } from 'draft-js';
import { useTranslation } from 'react-i18next';
import { RootState, useAppSelector } from '../../redux';
import LanguageToolApi, { LanguageToolMatch } from '../../services/LanguageTool';
import { detectMessageLanguage, normalizeUiLanguage } from '../../utils/detectMessageLanguage';
import { absoluteOffsetToBlockPosition, contentStateToPlainText } from './languageToolOffsets';
import { createLanguageToolDecorators } from './LanguageToolDecoration';

const DEBOUNCE_MS = 700;
const MIN_CHARS = 3;

type UseLanguageToolProofreadArgs = {
    editorState: EditorState;
    onEditorStateChange: (next: EditorState) => void;
    /** Wysiwyg wrapper — used to detect DOM focus before decorator bumps. */
    wrapperRef?: RefObject<HTMLElement | null>;
};

type BumpDecoratorsOptions = {
    /**
     * Whether this editor currently owns DOM focus.
     * When false, avoid Draft's forceSelection (it always sets hasFocus:true and
     * steals caret from sibling inputs — quote title, price fields, etc.).
     */
    hasDomFocus?: boolean;
};

/**
 * Force Draft.js / react-draft-wysiwyg to re-run decorator strategies when
 * match data changes without a content change.
 *
 * Clearing the decorator is required: EditorState.set with the same
 * CompositeDecorator reference leaves treeMap untouched (stale decorations).
 * rdw then re-applies its compositeDecorator in componentDidUpdate and rebuilds.
 *
 * forceSelection re-applies the current selection to the DOM after decoration
 * remounts (otherwise Ctrl-A / drag selections collapse visually) — but only
 * when this editor already has focus.
 */
export const bumpEditorStateForDecorators = (
    editorState: EditorState,
    options?: BumpDecoratorsOptions,
): EditorState => {
    const selection = editorState.getSelection();
    const cleared = EditorState.set(editorState, { decorator: null });
    const keepDomFocus = options?.hasDomFocus ?? selection.getHasFocus();
    if (!keepDomFocus)
        return EditorState.acceptSelection(cleared, selection.set('hasFocus', false));
    return EditorState.forceSelection(cleared, selection);
};

/** True when bumping decorators now would kill an in-progress selection (Ctrl-A). */
export const shouldDeferDecoratorBump = (editorState: EditorState): boolean => {
    const selection = editorState.getSelection();
    return selection.getHasFocus() && !selection.isCollapsed();
};

/**
 * react-draft-wysiwyg only builds CompositeDecorator once (constructor) from
 * `customDecorators`, then re-applies that same decorator on every editorState
 * prop change. Strategies must therefore read matches via a ref, and we bump
 * editorState when matches change so decorations re-scan.
 */
const editorHasDomFocus = (wrapperRef?: RefObject<HTMLElement | null>): boolean => {
    const root = wrapperRef?.current;
    if (!root || typeof document === 'undefined')
        return false;
    return root.contains(document.activeElement);
};

export const useLanguageToolProofread = ({
    editorState,
    onEditorStateChange,
    wrapperRef,
}: UseLanguageToolProofreadArgs) => {
    const { i18n } = useTranslation();
    const { setting } = useAppSelector((state: RootState) => state.settings);
    const enabled = !!setting?.languagetool?.enabled;

    const [matches, setMatches] = useState<LanguageToolMatch[]>([]);
    const matchesRef = useRef<LanguageToolMatch[]>([]);
    matchesRef.current = matches;

    const requestIdRef = useRef(0);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingBumpRef = useRef(false);
    const editorStateRef = useRef(editorState);
    editorStateRef.current = editorState;
    const onEditorStateChangeRef = useRef(onEditorStateChange);
    onEditorStateChangeRef.current = onEditorStateChange;
    const wrapperRefCurrent = wrapperRef;

    const content = editorState.getCurrentContent();

    const resolveLanguage = useCallback((plainText: string): string => {
        const configured = (setting?.languagetool?.default_language || 'auto').trim();
        if (configured && configured !== 'auto')
            return configured;

        const detected = detectMessageLanguage(plainText);
        if (detected)
            return detected;

        return normalizeUiLanguage(i18n.language) || 'auto';
    }, [i18n.language, setting?.languagetool?.default_language]);

    const applySuggestion = useCallback((match: LanguageToolMatch, replacement: string) => {
        const prev = editorStateRef.current;
        const prevContent = prev.getCurrentContent();
        const startPos = absoluteOffsetToBlockPosition(prevContent, match.offset);
        const endPos = absoluteOffsetToBlockPosition(
            prevContent,
            match.offset + match.length,
            { exclusiveEnd: true },
        );
        if (!startPos || !endPos || startPos.blockKey !== endPos.blockKey)
            return;

        const selection = new SelectionState({
            anchorKey: startPos.blockKey,
            anchorOffset: startPos.offset,
            focusKey: endPos.blockKey,
            focusOffset: endPos.offset,
            isBackward: false,
            hasFocus: true,
        });

        const nextContent = Modifier.replaceText(prevContent, selection, replacement);
        let nextState = EditorState.push(prev, nextContent, 'insert-characters');
        nextState = EditorState.forceSelection(nextState, nextContent.getSelectionAfter());
        setMatches([]);
        matchesRef.current = [];
        onEditorStateChangeRef.current(nextState);
    }, []);

    // Stable decorators: strategies always read the latest matches from the ref.
    const customDecorators = useMemo(() => {
        if (!enabled)
            return undefined;

        return createLanguageToolDecorators(
            () => matchesRef.current,
            applySuggestion,
        );
    }, [enabled, applySuggestion]);

    // When matches arrive without a content change, bump editorState so rdw
    // re-applies its compositeDecorator and strategies re-run against the ref.
    // Defer while the user has an expanded selection (Ctrl-A) so decoration
    // remounts do not clear it.
    useEffect(() => {
        if (!enabled)
            return;
        const current = editorStateRef.current;
        if (shouldDeferDecoratorBump(current)) {
            pendingBumpRef.current = true;
            return;
        }
        pendingBumpRef.current = false;
        onEditorStateChangeRef.current(bumpEditorStateForDecorators(current, {
            hasDomFocus: editorHasDomFocus(wrapperRefCurrent),
        }));
    }, [enabled, matches, wrapperRefCurrent]);

    // Flush a deferred decorator bump once selection collapses.
    useEffect(() => {
        if (!enabled || !pendingBumpRef.current)
            return;
        if (shouldDeferDecoratorBump(editorState))
            return;
        pendingBumpRef.current = false;
        onEditorStateChangeRef.current(bumpEditorStateForDecorators(editorState, {
            hasDomFocus: editorHasDomFocus(wrapperRefCurrent),
        }));
    }, [enabled, editorState, wrapperRefCurrent]);

    useEffect(() => {
        if (!enabled) {
            pendingBumpRef.current = false;
            setMatches([]);
            matchesRef.current = [];
            return undefined;
        }

        const plainText = contentStateToPlainText(content).trim();

        if (debounceRef.current)
            clearTimeout(debounceRef.current);

        if (plainText.length < MIN_CHARS) {
            setMatches([]);
            matchesRef.current = [];
            return undefined;
        }

        const requestId = ++requestIdRef.current;
        debounceRef.current = setTimeout(() => {
            const language = resolveLanguage(plainText);
            LanguageToolApi.check(plainText, language)
                .then((result) => {
                    if (requestId !== requestIdRef.current)
                        return;
                    const next = result.matches || [];
                    matchesRef.current = next;
                    setMatches(next);
                })
                .catch(() => {
                    if (requestId !== requestIdRef.current)
                        return;
                    matchesRef.current = [];
                    setMatches([]);
                });
        }, DEBOUNCE_MS);

        return () => {
            if (debounceRef.current)
                clearTimeout(debounceRef.current);
        };
    }, [enabled, content, resolveLanguage]);

    return {
        enabled,
        customDecorators,
    };
};

export default useLanguageToolProofread;
